/**
 * The colour palette offered when creating or editing a type or category.
 *
 * Curated rather than a free colour wheel. A block is a small rectangle with
 * text on it, and the two failure modes -- unreadable labels and two types you
 * cannot tell apart at a glance -- are both easy to walk into with an
 * unconstrained picker. Every swatch here passes AA against the label colour
 * that `labelColorOn` computes for it (asserted in the tests), and the hues are
 * spaced far enough apart to stay distinguishable at 22px tall.
 *
 * Free hex entry is still available for anyone who wants it; it is validated
 * and warned about rather than blocked.
 */


/** Eight hue families, four shades each. Ordered dark to light within a family. */
export const PALETTE: readonly string[] = [
  // Slate
  '#5C6A7E',
  '#6B7A90',
  '#8A97AA',
  '#9AA6B7',
  // Blue
  '#2E77C4',
  '#4A90D9',
  '#69A6E4',
  '#8ABBEE',
  // Teal
  '#1F8A8A',
  '#2FA5A5',
  '#57BDBD',
  '#84D2D2',
  // Green
  '#2F9963',
  '#4CAF7D',
  '#6BC295',
  '#8AD3AE',
  // Amber
  '#B06B2C',
  '#C9803E',
  '#DB9A5F',
  '#E8B583',
  // Red
  '#B03733',
  '#C03B37',
  '#D9534F',
  '#E4736F',
  // Purple
  '#7B3A75',
  '#96468E',
  '#B05FA8',
  '#C57ABD',
  // Pink
  '#B03A66',
  '#C7527D',
  '#D97298',
  '#E895B4',
]

/** Swatches per hue family, so the grid can lay out in rows that read as families. */
export const PALETTE_COLUMNS = 4

/**
 * Accepts `#abc`, `abc`, `#aabbcc` or `aabbcc` and returns `#AABBCC`.
 * Returns null for anything else, including empty input.
 */
export function normaliseHex(input: string): string | null {
  const s = input.trim().replace(/^#/, '')

  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    const [r, g, b] = [s[0], s[1], s[2]] as [string, string, string]
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase()
  }

  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s}`.toUpperCase()

  return null
}

/**
 * A colour for a newly created type or category.
 *
 * Walks the palette by how many things already use each colour, so the first
 * several new types are all visibly different from one another rather than all
 * arriving the same shade of blue.
 */
export function suggestColor(alreadyUsed: readonly string[]): string {
  const counts = new Map<string, number>()
  for (const hex of alreadyUsed) {
    const key = normaliseHex(hex)
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  let best = PALETTE[0] as string
  let bestCount = Infinity

  for (const swatch of PALETTE) {
    const count = counts.get(swatch) ?? 0
    if (count < bestCount) {
      best = swatch
      bestCount = count
      if (count === 0) break
    }
  }

  return best
}
